<div class="wizard-container" [class.read-only-mode]="isReadOnly()">
  <!-- Live Region for Announcements -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <!-- Modification Request Banner -->
  @if (hasModificationRequest()) {
    <div class="modification-request-banner mb-4 p-4 border-round-lg surface-0 border-2 border-orange-500 shadow-3" role="alert" aria-live="assertive">
      <div class="flex align-items-start gap-3">
        <div class="flex-shrink-0">
          <i class="pi pi-exclamation-triangle text-orange-500 text-3xl" aria-hidden="true"></i>
        </div>
        <div class="flex-1">
          <h3 class="text-orange-900 font-bold text-xl m-0 mb-2">
            <i class="pi pi-refresh mr-2" aria-hidden="true"></i>
            Richiesta di Modifica
          </h3>
          <p class="text-orange-800 text-base m-0 mb-3">
            AXA ha richiesto delle modifiche alla tua registrazione. Si prega di leggere attentamente le note seguenti e apportare le correzioni necessarie.
          </p>
          @if (modificationComment()) {
            <div class="surface-50 border-round p-3 border-left-3 border-orange-500">
              <p class="text-sm font-semibold text-600 m-0 mb-2">
                <i class="pi pi-comment mr-2" aria-hidden="true"></i>
                Note del Revisore:
              </p>
              <p class="text-base text-900 m-0 white-space-pre-wrap">{{ displayedComment() }}</p>
              @if (isCommentLong()) {
                <button 
                  type="button"
                  class="text-sm text-orange-600 hover:text-orange-700 font-semibold cursor-pointer border-none bg-transparent p-0 mt-2"
                  (click)="toggleModificationNotes()"
                  [attr.aria-expanded]="isModificationNotesExpanded()"
                  aria-controls="modification-notes">
                  <i class="pi mr-1" [class.pi-chevron-down]="!isModificationNotesExpanded()" [class.pi-chevron-up]="isModificationNotesExpanded()" aria-hidden="true"></i>
                  {{ isModificationNotesExpanded() ? 'Mostra meno' : 'Visualizza altro' }}
                </button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Success/Error Toast Messages -->
  <!-- Toast messages -->
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />

  <!-- Page Header (hidden in review mode) -->
  @if (!professionalIdInput()) {
  <section class="page-header mb-5">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-file-text header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1>{{ professionalsWizardService.isNetworkOnboarding() ? 'Allegati' : 'Questionari e Allegati' }}</h1>
            <p class="professional-info">
              @if (professionalSummaryBusinessName()) {
                <strong>{{ professionalSummaryBusinessName() }}</strong><br>
              }
              @if (professionalIdDisplay()) {
                ID: {{ professionalIdDisplay() }}
              }
              @if (professionalSummaryType()) {
                &nbsp;·&nbsp;{{ professionalSummaryType() }}
              }
            </p>
            <p class="subtitle">Raccogli i documenti richiesti</p>
          </div>
        </div>
        <!-- Completion Indicator -->
        <div
          class="completion-badge"
          [attr.aria-label]="isStepCompleted() ? 'Step completato' : 'Step non completato'"
        >
          @if (isStepCompleted()) {
            <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
            <span class="completion-text">Completato</span>
          } @else {
            <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
            <span class="completion-text">Da completare</span>
          }
        </div>
      </div>
    </section>
  }

  <!-- Read-Only Mode Banner (hidden in review mode) -->
  @if (isReadOnly() && !professionalIdInput()) {
    <div class="read-only-banner" role="alert">
      <div class="flex align-items-center gap-3 mb-2">
        <i class="pi pi-eye" aria-hidden="true"></i>
        <strong>Modalità Sola Lettura</strong>
      </div>
      <span>Stai visualizzando l'anagrafica inviata. I documenti sono disponibili solo per il download.</span>
    </div>
  }

  <!-- SEZIONE 1: QUESTIONARI (nascosta in modalità network body shop onboarding) -->
  @if (!shouldHideQuestionnaires()) {
  <section
    class="surface-0 border-round p-5 mb-4 shadow-1"
    role="region"
    aria-labelledby="section-questionari"
  >
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-file-edit text-2xl" style="color: #00125b" aria-hidden="true"></i>
      <h2 id="section-questionari" class="text-xl font-semibold m-0" style="color: #00125b">
        Questionari {{ tipologiaLabel() }}
      </h2>
    </div>
    <ul class="attachments-list" aria-label="Questionari disponibili">
      @if (questionnaireItems().length === 0) {
        <li class="no-items" role="status">Nessun questionario disponibile</li>
      }

      @for (item of questionnaireItems(); track item.id) {
        <li class="attachment-item">
          <div class="attachment-info">
            <div class="attachment-header">
              <i class="pi pi-file attachment-file-icon" aria-hidden="true"></i>
              <div class="attachment-details">
                <div class="attachment-title-row">
                  <span class="attachment-title">{{ item.title }}</span>
                  @if (getQuestionnaireAlerts(item).length > 0) {
                    <span
                      class="questionnaire-alert-badge"
                      [pTooltip]="'Domande con risposta anomala: ' + getQuestionnaireAlerts(item).map(a => a.questionText || a.alertMessage).join(', ')"
                      tooltipPosition="right"
                    >
                      <i class="pi pi-bell" aria-hidden="true"></i>
                      <span>{{ getQuestionnaireAlerts(item).length }}</span>
                    </span>
                  }
                  <!-- Tutti i questionari dinamici sono attualmente obbligatori -->
                  <span
                    class="required-indicator"
                    aria-label="Campo obbligatorio"
                    title="Campo obbligatorio"
                    >*</span
                  >
                </div>
                <div class="attachment-meta">
                  <div class="completion-badge" [attr.aria-label]="item.isUploaded ? 'Completato' : 'Da completare'">
                    @if (item.isUploaded) {
                      <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
                      <span class="completion-text">Completato</span>
                    } @else {
                      <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
                      <span class="completion-text">Da completare</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="attachment-actions">
            @if (item.templateUrl) {
              <button
                pButton
                type="button"
                label="Scarica"
                icon="pi pi-download"
                class="p-button-text p-button-sm action-button"
                (click)="downloadTemplate(item)"
                [attr.aria-label]="'Scarica template ' + item.title"
              ></button>
            }

            <!-- Se il questionario NON è stato completato (isUploaded è false) -->
            @if (!item.isUploaded && !isReadOnly()) {
              <button
                pButton
                type="button"
                label="Compila"
                icon="pi pi-file-edit"
                class="p-button-text p-button-sm action-button"
                (click)="compileQuestionnaire(item)"
                [attr.aria-label]="'Compila questionario ' + item.title"
              ></button>
            }
            <!-- Se il questionario è già stato completato (isUploaded è true) -->
            @else if (item.isUploaded) {
              <!-- Visualizza: disponibile SOLO in modalità readonly -->
              @if (isReadOnly()) {
                <button
                  pButton
                  type="button"
                  label="Visualizza"
                  icon="pi pi-eye"
                  class="p-button-text p-button-sm action-button"
                  (click)="viewQuestionnaire(item)"
                  [attr.aria-label]="'Visualizza questionario ' + item.title"
                ></button>
              }
              @if (item.answerId && item.id !== 'VRF_QUESTIONNAIRE') {
                <button
                  pButton
                  type="button"
                  label="Scarica PDF"
                  icon="pi pi-file-pdf"
                  class="p-button-text p-button-sm action-button"
                  (click)="downloadQuestionnairePdf(item)"
                  [attr.aria-label]="'Scarica PDF ' + item.title"
                ></button>
              }
              <!-- Modifica: disponibile SOLO se NON readonly -->
              @if (!isReadOnly()) {
                <button
                  pButton
                  type="button"
                  label="Modifica"
                  icon="pi pi-pencil"
                  class="p-button-text p-button-sm action-button"
                  (click)="compileQuestionnaire(item)"
                  [attr.aria-label]="'Modifica questionario ' + item.title"
                ></button>
              }
            }
          </div>
        </li>
      }
    </ul>
  </section>
  }

  <!-- SEZIONE 2: CHECKLIST (nascosta se non ci sono checklist) -->
  @if (checklistItems().length > 0) {
    <section
      class="surface-0 border-round p-5 mb-4 shadow-1"
      role="region"
      aria-labelledby="section-checklist"
    >
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-paperclip text-2xl" style="color: #00125b" aria-hidden="true"></i>
      <h2 id="section-checklist" class="text-xl font-semibold m-0" style="color: #00125b">
        {{ checklistSectionName() }}
      </h2>
    </div>

    @if (!isReadOnly() && checklistItems().length > 0) {
      <div
        class="flex align-items-center gap-2 p-3 mb-3 border-round bg-blue-50 border-1 border-blue-200"
        role="note"
        aria-label="Informazione sui modelli di riferimento"
      >
        <i class="pi pi-info-circle text-blue-600" aria-hidden="true"></i>
        <span class="text-sm text-blue-800">
          Per i documenti che prevedono un modello di riferimento, usa il pulsante
          <strong>Scarica Modello</strong> per ottenere il fac-simile da compilare prima dell'allegato.
        </span>
      </div>
    }

    <ul class="attachments-list" [attr.aria-label]="checklistSectionName() + ': ' + checklistItems().length + ' documenti'">
      @if (checklistItems().length === 0) {
        <li class="no-items" role="status">Nessun allegato checklist disponibile</li>
      }

      @for (item of checklistItems(); track item.id) {
        <li class="attachment-item">
          <div class="attachment-info">
            <div class="attachment-header">
              <i class="pi pi-file attachment-file-icon" aria-hidden="true"></i>
              <div class="attachment-details">
                <!-- Riga titolo con estensioni tra parentesi -->
                <div class="attachment-title-row">
                  <span class="attachment-title">
                    {{ item.title }}
                    @if ((item.allowedExtensions?.length ?? 0) > 0) {
                      <span
                        class="attachment-title-formats"
                        [attr.aria-label]="'Formati accettati: ' + item.allowedExtensions!.join(', ')"
                      >({{ item.allowedExtensions!.join(', ') }})</span>
                    }
                  </span>
                  @if (item.isMandatory) {
                    <span
                      class="required-indicator"
                      aria-label="Campo obbligatorio"
                      title="Campo obbligatorio"
                      >*</span
                    >
                  }
                </div>

                <!-- Riga scadenza (solo se il campo prevede una data) -->
                @if (item.hasExpirationDate) {
                  <div class="attachment-expiration-row">
                    @if (item.isUploaded && !isReadOnly()) {
                      <!-- Modalità modifica: datepicker inline + pulsante aggiorna (visibile solo quando modificata) -->
                      <label
                        [for]="'exp-date-' + item.id"
                        class="expiration-label"
                      >Data scadenza:</label>
                      <p-datepicker
                        [inputId]="'exp-date-' + item.id"
                        [ngModel]="item.expirationDate"
                        (ngModelChange)="onExpirationDateChange($event, item.sectionId, item.id)"
                        dateFormat="dd/mm/yy"
                        placeholder="gg/mm/aaaa"
                        appendTo="body"
                        styleClass="inline-datepicker-input"
                        [showIcon]="true"
                        [ariaLabel]="'Data scadenza per ' + item.title"
                      ></p-datepicker>
                      @if (dirtyExpirationItems().has(item.id)) {
                        <button
                          pButton
                          type="button"
                          label="Aggiorna data"
                          icon="pi pi-save"
                          class="p-button-outlined p-button-sm expiration-update-btn"
                          [loading]="savingExpirationFor() === item.id"
                          [disabled]="!item.expirationDate || !item.answerId"
                          (click)="saveItemExpirationDate(item)"
                          [attr.aria-label]="'Aggiorna la data di scadenza per ' + item.title"
                        ></button>
                      }
                    } @else {
                      <!-- Modalità sola lettura: testo statico -->
                      <span class="expiration-label">
                        <i class="pi pi-calendar" aria-hidden="true"></i>
                        Data scadenza:
                      </span>
                      @if (item.expirationDate) {
                        <time
                          class="expiration-value"
                          [attr.datetime]="item.expirationDate | date:'yyyy-MM-dd'"
                        >{{ item.expirationDate | date:'dd/MM/yyyy' }}</time>
                      } @else {
                        <span class="expiration-value expiration-value--empty" aria-label="Data di scadenza da inserire al caricamento">—</span>
                      }
                    }
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="attachment-actions">
            @if (item.templateUrl) {
              <button
                pButton
                type="button"
                label="Scarica Modello"
                icon="pi pi-file-import"
                class="p-button-text p-button-sm action-button"
                (click)="downloadTemplate(item)"
                title="Scarica il modello da usare come riferimento"
                [attr.aria-label]="'Scarica il modello per ' + item.title"
              ></button>
            }

            @if (!item.isUploaded && !isReadOnly()) {
              <div class="flex align-items-center gap-2">
                <button
                  pButton
                  type="button"
                  label="Allega"
                  icon="pi pi-paperclip"
                  class="p-button-text p-button-sm action-button"
                  (click)="openChecklistUploadDialog(item.sectionId, item.id)"
                  [attr.aria-label]="'Allega documento per ' + item.title"
                ></button>
                <i
                  class="pi pi-circle text-xl text-400"
                  role="img"
                  aria-label="Documento non ancora allegato"
                ></i>
              </div>
            } @else if (item.isUploaded) {
              <div class="flex align-items-center gap-2">
                <button
                  pButton
                  type="button"
                  label="Scarica documento"
                  icon="pi pi-download"
                  class="p-button-text p-button-sm action-button"
                  (click)="downloadUploadedFile(item)"
                  [attr.aria-label]="'Scarica il documento allegato per ' + item.title"
                ></button>
                @if (!isReadOnly()) {
                  <button
                    pButton
                    type="button"
                    label="Sostituisci documento"
                    icon="pi pi-arrow-right-arrow-left"
                    class="p-button-text p-button-sm action-button"
                    (click)="openChecklistUploadDialog(item.sectionId, item.id)"
                    [attr.aria-label]="'Sostituisci il documento allegato per ' + item.title"
                  ></button>
                }
                <i
                  class="pi pi-check-circle text-xl text-green-600"
                  role="img"
                  aria-label="Documento allegato"
                ></i>
              </div>
            }
          </div>
        </li>
      }
    </ul>
    </section>
  }

  <p-dialog
    [(visible)]="showChecklistUploadDialogValue"
    [modal]="true"
    [closable]="true"
    [closeOnEscape]="true"
    [dismissableMask]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="w-11 md:w-8 lg:w-6"
    ariaLabel="Dialog caricamento documento checklist"
    (onHide)="closeChecklistUploadDialog()"
  >
    <ng-template #header>
      <div class="flex align-items-center gap-2">
        <i class="pi pi-upload text-xl" style="color: #00125b" aria-hidden="true"></i>
        <span class="text-xl font-semibold">Carica Nuovo Documento</span>
      </div>
    </ng-template>

    @if (activeChecklistUploadItem(); as uploadItem) {
      <div class="flex flex-column gap-4">
        <!-- Messaggio informativo per sostituzione file -->
        @if (uploadItem.isUploaded) {
          <div class="p-3 border-round" style="background-color: #fef3c7; border: 1px solid #f59e0b;">
            <div class="flex align-items-start gap-2">
              <i class="pi pi-info-circle text-yellow-800" aria-hidden="true"></i>
              <div class="flex-1">
                <p class="m-0 text-sm font-semibold text-yellow-900">Sostituzione File</p>
                <p class="m-0 mt-1 text-sm text-yellow-800">
                  Il nuovo file sostituirà quello attualmente caricato. Il file precedente non sarà più disponibile.
                </p>
              </div>
            </div>
          </div>
        }

        <div>
          <label id="dialog-checklist-file-label" class="block mb-2 font-semibold"
            >Seleziona File</label
          >

          <p-fileupload
            #dialogUploader
            name="dialog-checklist-attachment"
            [auto]="false"
            [customUpload]="true"
            (uploadHandler)="onDialogUpload($event)"
            (onSelect)="onDialogFileSelect($event)"
            (onError)="onFileError($event, uploadItem)"
            [accept]="getAcceptAttribute(uploadItem)"
            [maxFileSize]="10000000"
            [disabled]="isSubmitting()"
            [showUploadButton]="false"
            [showCancelButton]="false"
            styleClass="custom-fileupload"
            [attr.aria-label]="'Carica file per ' + uploadItem.title"
            [attr.aria-labelledby]="'dialog-checklist-file-label'"
            [attr.aria-describedby]="'dialog-checklist-file-help dialog-upload-validation-error'"
            class="w-full"
          >
            <ng-template pTemplate="header"></ng-template>

            <ng-template
              pTemplate="content"
              let-files
              let-chooseCallback="chooseCallback"
              let-removeFileCallback="removeFileCallback"
            >
              @if (isSubmitting()) {
                <div class="flex align-items-center justify-content-center gap-2 p-5">
                  <i class="pi pi-spin pi-spinner text-2xl" aria-hidden="true"></i>
                  <span class="font-semibold">Caricamento in corso...</span>
                </div>
              } @else if (files && files.length === 0) {
                <div
                  class="flex flex-column align-items-center justify-content-center p-5 text-center"
                >
                  <i class="pi pi-cloud-upload text-6xl mb-3 text-400" aria-hidden="true"></i>
                  <p class="m-0 mb-2 font-semibold text-900">Trascina qui il documento</p>
                  <p class="m-0 mb-3 text-sm text-600">oppure</p>
                  <p-button
                    label="Scegli File"
                    icon="pi pi-folder-open"
                    (onClick)="chooseCallback()"
                    [outlined]="true"
                    ariaLabel="Seleziona file allegato da caricare"
                  ></p-button>
                </div>
              } @else {
                <div class="p-3">
                  @for (file of files; track file.name) {
                    <div
                      class="flex align-items-center justify-content-between p-3 border-1 border-200 border-round mb-2"
                    >
                      <div class="flex align-items-center gap-3">
                        <i class="pi pi-file text-3xl text-primary" aria-hidden="true"></i>
                        <div>
                          <div class="font-semibold text-900">{{ file.name }}</div>
                          <small class="text-600">{{ formatFileSize(file.size) }}</small>
                        </div>
                      </div>
                      <p-button
                        icon="pi pi-times"
                        [rounded]="true"
                        [text]="true"
                        severity="danger"
                        (onClick)="removeFileCallback(file); onDialogFileRemoved(dialogUploader)"
                        [ariaLabel]="'Rimuovi ' + file.name"
                      ></p-button>
                    </div>
                  }
                  <p-button
                    label="Scegli un Altro File"
                    icon="pi pi-folder-open"
                    (onClick)="chooseCallback()"
                    [outlined]="true"
                    size="small"
                    class="w-full mt-2"
                    ariaLabel="Seleziona un file diverso da caricare"
                  ></p-button>
                </div>
              }
            </ng-template>
          </p-fileupload>

          <small id="dialog-checklist-file-help" class="text-600 block mt-2">
            <strong>Formati supportati:</strong> {{ getAllowedExtensionsLabel(uploadItem) }}
          </small>
          <small class="text-600 block mt-1"><strong>Dimensione massima:</strong> 10 MB</small>

          @if (shouldShowUploadDialogError()) {
            <small
              id="dialog-upload-validation-error"
              class="upload-validation-error block mt-2"
              role="alert"
              aria-live="assertive"
            >
              {{ getUploadDialogValidationMessage() }}
            </small>
          }
        </div>

        @if (uploadItem.hasExpirationDate) {
          <div class="expiration-field">
            <label class="expiration-date-label" [attr.for]="'expiration-date-' + uploadItem.id"
              >Scadenza Documento <span class="text-red-600">*</span></label
            >
            <p-datepicker
              [inputId]="'expiration-date-' + uploadItem.id"
              dateFormat="dd/mm/yy"
              placeholder="DD/MM/YYYY"
              [ngModel]="getExpirationDateAsDate(uploadItem)"
              (ngModelChange)="onExpirationDateChange($event, uploadItem.sectionId, uploadItem.id)"
              [showIcon]="false"
              [showButtonBar]="false"
              appendTo="body"
              aria-label="Scadenza Documento"
              [attr.aria-required]="'true'"
              [attr.aria-invalid]="
                shouldShowUploadDialogError() && !uploadItem.expirationDate ? 'true' : 'false'
              "
              [attr.aria-describedby]="'expiration-date-help dialog-upload-validation-error'"
            />
            <small id="expiration-date-help" class="text-600"
              >Campo obbligatorio se richiesto.</small
            >
          </div>
        }
      </div>

      <ng-template #footer>
        <p-button
          label="Annulla"
          severity="secondary"
          [outlined]="true"
          icon="pi pi-times"
          (onClick)="closeChecklistUploadDialog()"
          [disabled]="isSubmitting()"
          ariaLabel="Annulla e chiudi il dialog di caricamento"
        ></p-button>
        <p-button
          label="Carica"
          icon="pi pi-upload"
          (onClick)="dialogUploader.upload()"
          [disabled]="isSubmitting() || !canUploadCurrentChecklistItem()"
          [loading]="isSubmitting()"
          ariaLabel="Carica documento nel sistema"
        ></p-button>
      </ng-template>
    }
  </p-dialog>
</div>
